<template>
  <div class="campaign-form">
    <div class="campaign-form__header">
      <div class="d-flex align-items-center">
        <b-icon
          icon="arrow-left-square-fill"
          class="back-arrow h3 mb-0 cursor-pointer"
          @click="$router.go(-1)"
        ></b-icon>
        <div class="ml-3">
          <h1 class="page-title">
            Mantendor Campañas / {{ esEdicion ? 'Editar' : 'Crear' }} Correo
          </h1>
          <p class="page-subtitle">
            Configura los correos automáticos para clientes
          </p>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading && esEdicion" class="text-center py-5">
      <b-spinner variant="primary" label="Cargando..."></b-spinner>
      <p class="mt-2 text-muted">Cargando campaña...</p>
    </div>

    <!-- Formulario -->
    <div v-else class="form-layout">
      <!-- Columna izquierda: Formulario -->
      <div class="form-content">
        <!-- Paso 1: Reglas de Envío -->
        <div class="form-section">
          <div class="section-header">
            <h2 class="section-title">
              <span class="step-number">1</span>
              Reglas de Envío
            </h2>
          </div>

          <div class="section-body">
            <!-- Nombre Campaña -->
            <b-form-group
              label="Nombre Campaña"
              label-for="nombre"
              :state="validarCampo('nombre')"
              :invalid-feedback="errores.nombre"
              class="mb-3"
            >
              <b-form-input
                id="nombre"
                v-model="form.nombre"
                placeholder="Ingresa un Nombre para identificar esta campaña."
                maxlength="100"
                :state="validarCampo('nombre')"
              />
            </b-form-group>

            <!-- Tipo de OT -->
            <b-form-group
              label="Tipo de OT"
              :state="validarCampo('tipo_ot')"
              :invalid-feedback="errores.tipo_ot"
              class="mb-3"
            >
              <div class="tipo-ot-buttons">
                <button
                  type="button"
                  class="tipo-ot-btn"
                  :class="{ active: form.tipo_ot === 'PRESUPUESTO' }"
                  @click="seleccionarTipoOT('PRESUPUESTO')"
                  data-tipo="PRESUPUESTO"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path 
                      d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z" 
                      fill="currentColor"
                    />
                  </svg>
                  <span>Presupuesto</span>
                </button>
                <button
                  type="button"
                  class="tipo-ot-btn"
                  :class="{ active: form.tipo_ot === 'GARANTIA' }"
                  @click="seleccionarTipoOT('GARANTIA')"
                  data-tipo="GARANTIA"
                >
                  <font-awesome-icon :icon="['fas', 'shield-alt']" />
                  <span>Garantía</span>
                </button>
              </div>
            </b-form-group>

            <!-- Fila con Fecha, Operación y Días -->
            <b-row>
              <b-col md="4">
                <b-form-group
                  label="Fecha de Referencia"
                  label-for="fecha_referencia"
                  :state="validarCampo('fecha_referencia')"
                  :invalid-feedback="errores.fecha_referencia"
                >
                  <b-form-select
                    id="fecha_referencia"
                    v-model="form.fecha_referencia"
                    :options="opcionesFechaReferencia"
                    :disabled="fechaReferenciaDisabled"
                    :state="validarCampo('fecha_referencia')"
                  />
                </b-form-group>
              </b-col>

              <b-col md="4">
                <b-form-group
                  label="Operación"
                  label-for="operacion"
                  :state="validarCampo('operacion')"
                  :invalid-feedback="errores.operacion"
                >
                  <b-form-select
                    id="operacion"
                    v-model="form.operacion"
                    :options="OPERACION_CHOICES.map(o => ({ value: o.value, text: o.label }))"
                    :state="validarCampo('operacion')"
                  />
                </b-form-group>
              </b-col>

              <b-col md="4">
                <b-form-group
                  label="N° de Días"
                  label-for="dias"
                  :state="validarCampo('dias')"
                  :invalid-feedback="errores.dias"
                >
                  <b-form-input
                    id="dias"
                    v-model.number="form.dias"
                    type="number"
                    min="1"
                    placeholder="5"
                    :state="validarCampo('dias')"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Mensaje Informativo -->
            <b-alert show variant="info" class="mensaje-informativo">
              <i class="fas fa-info-circle mr-2"></i>
              {{ mensajeInformativo }}
            </b-alert>
          </div>
        </div>

        <!-- Paso 2: Variantes -->
        <div class="form-section">
          <div class="section-header">
            <h2 class="section-title">
              <span class="step-number">2</span>
              Variantes de Correo
            </h2>

          </div>

          <div class="section-body">
            <!-- Variante 1 -->
            <div class="variante-card">
              <h3 class="variante-title">Variante 01</h3>
              
              <!-- Asunto -->
              <b-form-group
                label="Asunto"
                label-for="asunto_1"
                :state="validarVariante(0, 'asunto')"
                :invalid-feedback="getErrorVariante(0, 'asunto')"
              >
                <b-form-input
                  id="asunto_1"
                  v-model="form.variantes[0].asunto"
                  placeholder="Ingresa un Asunto para esta Variante"
                  maxlength="200"
                  :state="validarVariante(0, 'asunto')"
                  @focus="editorActivo = 'asunto_1'"
                  name="variante_1_asunto"
                />
                <small class="text-muted">
                  {{ form.variantes[0].asunto.length }} / 200 caracteres
                </small>
              </b-form-group>

              <!-- Cuerpo -->
              <b-form-group
                label="Cuerpo del Correo"
                :state="validarVariante(0, 'cuerpo')"
                :invalid-feedback="getErrorVariante(0, 'cuerpo')"
              >
                <EmailEditor
                  ref="editor_1"
                  v-model="form.variantes[0].cuerpo"
                  placeholder="Escribe el contenido del correo..."
                  :min-height="180"
                  @focus="editorActivo = 'editor_1'"
                  class="quill-editor-1"
                />
              </b-form-group>
              <p class="text-muted small mt-1 mb-0">
                Última modificación real:
                <span class="font-weight-bold">{{ formatearFechaModificacion(fechasModificacion[0]) }}</span>
              </p>
            </div>

            <!-- Variante 2 -->
            <div class="variante-card">
              <h3 class="variante-title">Variante 02</h3>
              
              <!-- Asunto -->
              <b-form-group
                label="Asunto"
                label-for="asunto_2"
                :state="validarVariante(1, 'asunto')"
                :invalid-feedback="getErrorVariante(1, 'asunto')"
              >
                <b-form-input
                  id="asunto_2"
                  v-model="form.variantes[1].asunto"
                  placeholder="Ingresa un Asunto para esta Variante"
                  maxlength="200"
                  :state="validarVariante(1, 'asunto')"
                  @focus="editorActivo = 'asunto_2'"
                  name="variante_2_asunto"
                />
                <small class="text-muted">
                  {{ form.variantes[1].asunto.length }} / 200 caracteres
                </small>
              </b-form-group>

              <!-- Cuerpo -->
              <b-form-group
                label="Cuerpo del Correo"
                :state="validarVariante(1, 'cuerpo')"
                :invalid-feedback="getErrorVariante(1, 'cuerpo')"
              >
                <EmailEditor
                  ref="editor_2"
                  v-model="form.variantes[1].cuerpo"
                  placeholder="Escribe el contenido del correo..."
                  :min-height="180"
                  @focus="editorActivo = 'editor_2'"
                  class="quill-editor-2"
                />
              </b-form-group>
              <p class="text-muted small mt-1 mb-0">
                Última modificación real:
                <span class="font-weight-bold">{{ formatearFechaModificacion(fechasModificacion[1]) }}</span>
              </p>
            </div>

            <!-- Variante 3 -->
            <div class="variante-card">
              <h3 class="variante-title">Variante 03</h3>
              
              <!-- Asunto -->
              <b-form-group
                label="Asunto"
                label-for="asunto_3"
                :state="validarVariante(2, 'asunto')"
                :invalid-feedback="getErrorVariante(2, 'asunto')"
              >
                <b-form-input
                  id="asunto_3"
                  v-model="form.variantes[2].asunto"
                  placeholder="Ingresa un Asunto para esta Variante"
                  maxlength="200"
                  :state="validarVariante(2, 'asunto')"
                  @focus="editorActivo = 'asunto_3'"
                  name="variante_3_asunto"
                />
                <small class="text-muted">
                  {{ form.variantes[2].asunto.length }} / 200 caracteres
                </small>
              </b-form-group>

              <!-- Cuerpo -->
              <b-form-group
                label="Cuerpo del Correo"
                :state="validarVariante(2, 'cuerpo')"
                :invalid-feedback="getErrorVariante(2, 'cuerpo')"
              >
                <EmailEditor
                  ref="editor_3"
                  v-model="form.variantes[2].cuerpo"
                  placeholder="Escribe el contenido del correo..."
                  :min-height="180"
                  @focus="editorActivo = 'editor_3'"
                  class="quill-editor-3"
                />
              </b-form-group>
              <p class="text-muted small mt-1 mb-0">
                Última modificación real:
                <span class="font-weight-bold">{{ formatearFechaModificacion(fechasModificacion[2]) }}</span>
              </p>
            </div>
          </div>
        </div>

        <!-- Botones de acción -->
        <div class="form-actions">
          <b-button variant="outline-secondary" @click="cancelar">
            Cancelar
          </b-button>
          <b-button
            variant="primary"
            @click="guardar"
            :disabled="guardando"
          >
            <b-spinner v-if="guardando" small class="mr-2"></b-spinner>
            Guardar Correo
          </b-button>
        </div>
      </div>

      <!-- Columna derecha: Panel de Etiquetas -->
      <div class="tags-sidebar">
        <TagsPanel @tag-selected="insertarEtiqueta" />
      </div>
    </div>

    <!-- Modal de Envío de Prueba -->
    <b-modal
      v-model="showTestEmailModal"
      title="Enviar Correo de Prueba"
      size="md"
      @ok="handleOkEnvioPrueba"
      ok-title="Enviar Prueba"
      cancel-title="Cancelar"
      :ok-disabled="enviandoPrueba || !emailPrueba || !variantePrueba || !emailValido"
      :no-close-on-backdrop="enviandoPrueba"
      :no-close-on-esc="enviandoPrueba"
    >
      <p class="mb-3">
        Enviaremos un correo de prueba con datos ficticios para que puedas verificar cómo se verá el correo final.
      </p>

      <!-- Correo de destino -->
      <b-form-group
        label="Correo de destino"
        label-for="email-prueba"
        description="Ingresa el correo donde deseas recibir la prueba"
      >
        <b-form-input
          id="email-prueba"
          v-model="emailPrueba"
          type="email"
          placeholder="ejemplo@samtek.cl"
          :state="emailPrueba ? validarEmail(emailPrueba) : null"
        />
      </b-form-group>

      <!-- Selector de variante -->
      <b-form-group
        label="Selecciona la variante a probar"
        label-for="variante-prueba"
      >
        <b-form-radio-group
          id="variante-prueba"
          v-model="variantePrueba"
          :options="opcionesVariantes"
          stacked
        />
      </b-form-group>

      <!-- Nota informativa -->
      <b-alert show variant="info" class="mt-3 mb-0">
        <small>
          <i class="fas fa-info-circle mr-1"></i>
          El correo incluirá datos de ejemplo (Nombre: Juan Pérez, OT: 96654, etc.)
        </small>
      </b-alert>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment';
import 'moment/locale/es';
import { mapState, mapActions } from 'vuex';
import EmailEditor from '@/components/Campaign/EmailEditor.vue';
import TagsPanel from '@/components/Campaign/TagsPanel.vue';
import {
  validarCampaign,
  TIPO_OT_CHOICES,
  FECHA_REFERENCIA_CHOICES,
  OPERACION_CHOICES
} from '@/utils/campaignValidation';

export default {
  name: 'CampaignForm',
  components: {
    EmailEditor,
    TagsPanel
  },
  data() {
    return {
      TIPO_OT_CHOICES,
      FECHA_REFERENCIA_CHOICES,
      OPERACION_CHOICES,
      form: {
        nombre: '',
        tipo_ot: '',
        fecha_referencia: '',
        operacion: '',
        dias: null,
        activo: true,
        variantes: [
          { variant_number: 1, asunto: '', cuerpo: '' },
          { variant_number: 2, asunto: '', cuerpo: '' },
          { variant_number: 3, asunto: '', cuerpo: '' }
        ]
      },
      fechasModificacion: [null, null, null],
      errores: {},
      guardando: false,
      enviandoPrueba: false,
      editorActivo: null,
      showTestEmailModal: false,
      emailPrueba: '',
      variantePrueba: null
    };
  },
  computed: {
    ...mapState('Campaign', ['loading', 'error']),
    esEdicion() {
      return !!this.$route.params.id;
    },
    campaignId() {
      return this.$route.params.id;
    },
    opcionesFechaReferencia() {
      return FECHA_REFERENCIA_CHOICES.map(opt => ({
        value: opt.value,
        text: opt.label
      }));
    },
    fechaReferenciaDisabled() {
      return this.form.tipo_ot === 'PRESUPUESTO';
    },
    mensajeInformativo() {
      if (!this.form.tipo_ot || !this.form.operacion || !this.form.dias || !this.form.fecha_referencia) {
        return 'Completa los campos para ver la regla configurada';
      }

      const tipoLabel = this.form.tipo_ot === 'GARANTIA' ? 'Garantía' : 'Presupuesto';
      const fechaLabel = this.form.fecha_referencia === 'COMPRA' ? 'Compra' : 'Cierre';
      const operacionLabel = this.form.operacion === 'SUMA' ? 'después' : 'antes';
      
      return `Este correo se enviará a Clientes de '${tipoLabel}', ${this.form.dias} día(s) ${operacionLabel} de su fecha de '${fechaLabel}'`;
    },
    opcionesVariantes() {
      return this.form.variantes
        .filter(v => v.asunto && v.cuerpo) // Solo variantes con contenido
        .map(v => ({
          value: v.variant_number,
          text: `Variante ${v.variant_number}: ${v.asunto.substring(0, 50)}${v.asunto.length > 50 ? '...' : ''}`
        }));
    },
    emailValido() {
      if (!this.emailPrueba) return false;
      return this.validarEmail(this.emailPrueba);
    }
  },
  async mounted() {
    if (this.esEdicion) {
      await this.cargarCampana();
    }
  },
  methods: {
    ...mapActions('Campaign', ['fetchCampana', 'crearCampana', 'actualizarCampana']),
    async cargarCampana() {
      try {
        const campana = await this.fetchCampana(this.campaignId);
        this.form = {
          nombre: campana.nombre,
          tipo_ot: campana.tipo_ot,
          fecha_referencia: campana.fecha_referencia,
          operacion: campana.operacion,
          dias: campana.dias,
          activo: campana.activo,
          variantes: campana.variantes.map(v => ({
            variant_number: v.variant_number,
            asunto: v.asunto,
            cuerpo: v.cuerpo
          }))
        };
        // Guardar fechas de última modificación real (solo lectura, fuera del form)
        this.fechasModificacion = campana.variantes.map(
          v => v.fecha_ultima_modificacion || null
        );
      } catch (error) {
        this.$bvToast.toast('Error al cargar la campaña', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        this.$router.push({ name: 'Campañas' });
      }
    },
    formatearFechaModificacion(iso) {
      if (!iso) return 'Sin registro';
      return moment(iso).locale('es').format("DD [de] MMMM YYYY [a las] HH:mm");
    },
    seleccionarTipoOT(tipo) {
      this.form.tipo_ot = tipo;
      
      // Si es Presupuesto, forzar fecha_referencia a CIERRE
      if (tipo === 'PRESUPUESTO') {
        this.form.fecha_referencia = 'CIERRE';
      }
    },
    insertarEtiqueta(tag) {
      // Determinar dónde insertar la etiqueta
      if (this.editorActivo) {
        if (this.editorActivo.startsWith('asunto_')) {
          const varianteNum = parseInt(this.editorActivo.split('_')[1], 10);
          const input = document.getElementById(`asunto_${varianteNum}`);
          if (input) {
            const start = input.selectionStart;
            const end = input.selectionEnd;
            const text = this.form.variantes[varianteNum - 1].asunto;
            this.form.variantes[varianteNum - 1].asunto = 
              text.substring(0, start) + tag + text.substring(end);
            
            // Enfocar y posicionar cursor después de la etiqueta
            this.$nextTick(() => {
              input.focus();
              input.setSelectionRange(start + tag.length, start + tag.length);
            });
          }
        } else if (this.editorActivo.startsWith('editor_')) {
          const editorNum = parseInt(this.editorActivo.split('_')[1], 10);
          const editorRef = this.$refs[`editor_${editorNum}`];
          if (editorRef) {
            // El ref puede ser un array o un objeto único, manejamos ambos casos
            const editor = Array.isArray(editorRef) ? editorRef[0] : editorRef;
            if (editor && typeof editor.insertarEtiqueta === 'function') {
              editor.insertarEtiqueta(tag);
            }
          }
        }
      } else {
        // Si no hay editor activo, mostrar un mensaje
        this.$bvToast.toast('Por favor, haz clic en un campo de Asunto o Cuerpo antes de insertar una etiqueta.', {
          title: 'Información',
          variant: 'info',
          solid: true,
          autoHideDelay: 3000
        });
      }
    },
    validarCampo(campo) {
      if (Object.keys(this.errores).length === 0) return null;
      return this.errores[campo] ? false : null;
    },
    validarVariante(index, campo) {
      if (Object.keys(this.errores).length === 0) return null;
      const errorKey = `variante_${index + 1}`;
      return (this.errores[errorKey] && this.errores[errorKey][campo]) ? false : null;
    },
    getErrorVariante(index, campo) {
      const errorKey = `variante_${index + 1}`;
      return (this.errores[errorKey] && this.errores[errorKey][campo]) || '';
    },
    async enviarPrueba() {
      // Validar que haya al menos una variante con contenido
      const variantesValidas = this.form.variantes.filter(v => v.asunto && v.cuerpo);
      
      if (variantesValidas.length === 0) {
        this.$bvToast.toast('Debes completar al menos una variante antes de enviar la prueba', {
          title: 'Validación',
          variant: 'warning',
          solid: true
        });
        return;
      }

      // Obtener email del usuario actual si está disponible
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      this.emailPrueba = user.email || '';
      
      // Preseleccionar la primera variante válida
      this.variantePrueba = variantesValidas[0].variant_number;
      
      // Abrir modal
      this.showTestEmailModal = true;
    },
    handleOkEnvioPrueba(bvModalEvt) {
      // Prevenir el cierre automático del modal
      bvModalEvt.preventDefault();
      // Ejecutar el envío
      this.confirmarEnvioPrueba();
    },
    async confirmarEnvioPrueba() {
      this.enviandoPrueba = true;
      
      try {
        // Buscar la variante seleccionada
        const variante = this.form.variantes.find(v => v.variant_number === this.variantePrueba);
        
        if (!variante) {
          throw new Error('Variante no encontrada');
        }

        // Importar el servicio
        const campaignService = (await import('@/services/campaignService')).default;
        
        // Enviar correo de prueba
        const resultado = await campaignService.enviarCorreoPrueba({
          email_destinatario: this.emailPrueba,
          asunto: variante.asunto,
          cuerpo: variante.cuerpo,
          variante_id: this.variantePrueba
        });

        this.$bvToast.toast(resultado.message || `Correo de prueba enviado a ${this.emailPrueba}`, {
          title: 'Éxito',
          variant: 'success',
          solid: true,
          autoHideDelay: 5000
        });

        // Cerrar modal
        this.showTestEmailModal = false;
        
      } catch (error) {
        console.error('Error al enviar correo de prueba:', error);
        this.$bvToast.toast(
          error.response?.data?.error || 'Error al enviar el correo de prueba',
          {
            title: 'Error',
            variant: 'danger',
            solid: true
          }
        );
      } finally {
        this.enviandoPrueba = false;
      }
    },
    validarEmail(email) {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return regex.test(email);
    },
    async guardar() {
      // Validar formulario
      const validacion = validarCampaign(this.form);
      
      if (!validacion.valido) {
        this.errores = validacion.errores;
        this.$bvToast.toast('Por favor corrige los errores en el formulario', {
          title: 'Validación',
          variant: 'warning',
          solid: true
        });
        return;
      }

      this.errores = {};
      this.guardando = true;

      try {
        if (this.esEdicion) {
          await this.actualizarCampana({
            id: this.campaignId,
            datos: this.form
          });
          this.$bvToast.toast('Campaña actualizada correctamente', {
            title: 'Éxito',
            variant: 'success',
            solid: true
          });
        } else {
          await this.crearCampana(this.form);
          this.$bvToast.toast('Campaña creada correctamente', {
            title: 'Éxito',
            variant: 'success',
            solid: true
          });
        }
        
        this.$router.push({ name: 'Campañas' });
      } catch (error) {
        // Manejar errores del backend
        if (error.response && error.response.data) {
          const backendErrors = error.response.data;
          // Mapear errores del backend al formato del formulario
          this.errores = backendErrors;
        }
        
        this.$bvToast.toast(
          (error.response && error.response.data && error.response.data.detail) || 'Error al guardar la campaña',
          {
            title: 'Error',
            variant: 'danger',
            solid: true
          }
        );
      } finally {
        this.guardando = false;
      }
    },
    cancelar() {
      this.$router.push({ name: 'Campañas' });
    }
  }
};
</script>

<style scoped>
.campaign-form {
  padding: 24px;
  background: #F5F6FA;
  min-height: calc(100vh - 60px);
}

.campaign-form__header {
  margin-bottom: 24px;
}

::v-deep .back-arrow {
  color: #FECE2F;
  background-color: black;
  border-radius: 15px;
  cursor: pointer;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #000;
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.form-layout {
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 24px;
  align-items: start;
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.section-header {
  background: #F9FAFB;
  padding: 20px 24px;
  border-bottom: 1px solid #E5E7EB;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #23201C;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #FECE2F;
  color: #23201C;
  border-radius: 50%;
  font-size: 16px;
  font-weight: 700;
}

.section-subtitle {
  font-size: 13px;
  color: #525252;
  margin: 8px 0 0 44px;
}

.section-body {
  padding: 24px;
}

/* Form Labels */
.section-body ::v-deep .form-group label {
  font-weight: 600;
  color: #1F2937;
  margin-bottom: 8px;
  font-size: 14px;
}

.section-body ::v-deep .form-control {
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 14px;
  padding: 10px 12px;
}

.section-body ::v-deep .form-control:focus {
  border-color: #3B82F6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Tipo de OT Buttons */
.tipo-ot-buttons {
  display: flex;
  gap: 12px;
}

.tipo-ot-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 24px;
  border: 2px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 600;
  min-height: 70px;
  min-width: 140px;
  /* Estilo por defecto (no seleccionado) - Gris */
  background: #D1D5DB;
  color: #374151;
  border-color: #9CA3AF;
}

.tipo-ot-btn svg {
  width: 24px;
  height: 24px;
  color: #374151; /* Gris por defecto */
}

.tipo-ot-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* Estilo cuando está seleccionado (activo) - Amarillo */
.tipo-ot-btn.active {
  background: #FEF3C7;
  color: #78350F;
  border-color: #FDE68A;
  border-width: 3px;
  border-color: #D97706;
  box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.2);
}

.tipo-ot-btn.active svg {
  color: #78350F; /* Amarillo cuando activo */
}

/* Mensaje Informativo */
.mensaje-informativo {
  margin-top: 20px;
  margin-bottom: 0;
  font-size: 14px;
  line-height: 1.5;
  background: #DBEAFE;
  border: 1px solid #93C5FD;
  color: #1E40AF;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.mensaje-informativo i {
  font-size: 16px;
  flex-shrink: 0;
}

/* Variantes */
.variante-card {
  padding: 24px;
  border: none;
  border-radius: 8px;
  margin-bottom: 20px;
  background: #fff;
}

.variante-card:last-child {
  margin-bottom: 0;
}

.variante-title {
  font-size: 16px;
  font-weight: 600;
  color: #23201C;
  margin: 0 0 20px 0;
  padding-bottom: 0;
  border-bottom: none;
}

/* Tags Sidebar */
.tags-sidebar {
  position: sticky;
  top: 24px;
}

/* Form Actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Responsive */
@media (max-width: 992px) {
  .form-layout {
    grid-template-columns: 1fr;
  }
  
  .tags-sidebar {
    position: static;
  }
}
</style>
